import React from "react";

import moment from "moment-timezone";
import { Link as RouterLink } from "react-router-dom";

import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "../../UI/MainPaper";
import SectionTitle, { ActionButton } from "./SectionTitle";

import EditIcon from "@material-ui/icons/Edit";
import LaunchIcon from "@material-ui/icons/Launch";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  link: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
}));

const LinkRouter = (props) => <Link {...props} component={RouterLink} />;

const formatPeople = (peopleArr = []) =>
  peopleArr
    .filter(({ qty }) => qty > 0)
    .map(({ identifier, qty }) => `${qty} ${identifier}`)
    .join(" · ");

// One label/value row
export const StayRow = ({ label, children }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="baseline"
    py={1}
    style={{ gap: 16 }}
  >
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Box textAlign="right">{children}</Box>
  </Box>
);

const PageLink = ({ to, label }) => {
  const classes = useStyles();

  return (
    <LinkRouter to={to} className={classes.link}>
      <LaunchIcon style={{ fontSize: 14 }} />
      <Typography variant="caption">{label}</Typography>
    </LinkRouter>
  );
};

const DateRows = ({ check_in, check_out, nights }) => (
  <>
    <StayRow label="Check In">
      <Typography variant="body2">
        {moment(check_in).utc().format("ll")}
      </Typography>
    </StayRow>
    <StayRow label="Check Out">
      <Typography variant="body2">
        {moment(check_out).utc().format("ll")}
      </Typography>
    </StayRow>
    <StayRow label="Nights">
      <Typography variant="body2">{nights}</Typography>
    </StayRow>
  </>
);

const CouponRow = ({ coupon }) => (
  <StayRow label="Coupon">
    {coupon ? (
      <>
        <Typography variant="body2">
          {coupon.name} ({coupon.coupon_code})
        </Typography>
        <Typography variant="caption" color="error" display="block">
          {coupon.type === "currency"
            ? `– $${coupon.discount_amount}`
            : `${coupon.discount_amount}% OFF`}
        </Typography>
        <PageLink
          to={`/coupons/${coupon.coupon_id}`}
          label="Go to Coupon page"
        />
      </>
    ) : (
      <Typography variant="body2" color="textSecondary">
        None
      </Typography>
    )}
  </StayRow>
);

export const StayPanel = ({
  packageObj,
  peopleArr,
  check_in,
  check_out,
  coupon,
  onEdit,
}) => {
  const classes = useStyles();
  const {
    package_name,
    hotel_name,
    hotel_address,
    room_name,
    room_qty,
    night_stay,
    originals,
  } = packageObj;

  return (
    <Paper className={classes.paper}>
      <SectionTitle
        spread
        title="Booking Details"
        action={
          <ActionButton icon={<EditIcon />} onClick={onEdit}>
            Edit
          </ActionButton>
        }
      />

      <StayRow label="Package">
        <Typography variant="body2">{package_name}</Typography>
      </StayRow>
      <Divider />
      <DateRows check_in={check_in} check_out={check_out} nights={night_stay} />
      <StayRow label="People">
        <Typography variant="body2">{formatPeople(peopleArr)}</Typography>
      </StayRow>
      <Divider />
      <StayRow label="Hotel">
        <Typography variant="body2">{hotel_name}</Typography>
        <Typography variant="caption" color="textSecondary" display="block">
          {hotel_address}
        </Typography>
        <PageLink
          to={`/hotels/${originals.hotel_id}`}
          label="Go to Hotel page"
        />
      </StayRow>
      <StayRow label="Room">
        <Typography variant="body2">
          {room_qty} × {room_name}
        </Typography>
      </StayRow>
      <Divider />
      <CouponRow coupon={coupon} />
    </Paper>
  );
};

export const CondoStayPanel = ({
  condoObj,
  peopleArr,
  check_in,
  check_out,
  coupon,
}) => {
  const classes = useStyles();
  const { condo_address, condo_name, night_stay } = condoObj;

  return (
    <Paper className={classes.paper}>
      <SectionTitle title="Booking Details" />

      <StayRow label="Condo">
        <Typography variant="body2">{condo_name}</Typography>
        <Typography variant="caption" color="textSecondary" display="block">
          {condo_address}
        </Typography>
      </StayRow>
      <Divider />
      <DateRows check_in={check_in} check_out={check_out} nights={night_stay} />
      <StayRow label="People">
        <Typography variant="body2">{formatPeople(peopleArr)}</Typography>
      </StayRow>
      <Divider />
      <CouponRow coupon={coupon} />
    </Paper>
  );
};
